import { useEffect, useRef, useState, useCallback } from "react";

/**
 * WebSocket hook for community chat.
 * Auto-reconnects with exponential backoff.
 */
export function useChatSocket(onEvent, enabled = true) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(0);
  const closedRef = useRef(false);
  const [status, setStatus] = useState("disconnected"); // disconnected, connecting, connected
  const handlerRef = useRef(onEvent);

  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  const connect = useCallback(() => {
    if (!enabled) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws/chat";
    setStatus("connecting");

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      setStatus("disconnected");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectRef.current = 0;
      setStatus("connected");
    };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        handlerRef.current?.(data);
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      setStatus("disconnected");
      if (closedRef.current) return;
      const delay = Math.min(1000 * Math.pow(2, reconnectRef.current), 15000);
      reconnectRef.current += 1;
      setTimeout(() => connect(), delay);
    };
    ws.onerror = () => { ws.close(); };
  }, [enabled]);

  useEffect(() => {
    closedRef.current = false;
    if (enabled) connect();
    return () => {
      closedRef.current = true;
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const send = useCallback((payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  return { status, send };
}
